/**
 * Command Palette (⌘K / Ctrl+K) — global keyboard-driven navigator.
 *
 * Registers a global hotkey and renders a shadcn CommandDialog with three
 * groups: Modules (from the sidebar NAV config), Service Users (from the
 * clients directory) and Care Plans (from client_care_planning). Actions
 * navigate the user straight to the record they picked.
 *
 * Client and care-plan data is fetched once on first open and cached in
 * component state to keep the palette instant on subsequent opens.
 */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup,
  CommandItem, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
  LayoutDashboard, Users2, ClipboardCheck, Building2, ArrowRight, ShieldCheck,
} from "lucide-react";
import { NAV } from "@/config/modules";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATIC_ROUTES = [
  { path: "/",                    label: "Dashboard",              icon: LayoutDashboard, testid: "cmd-static-dashboard" },
  { path: "/clients",             label: "Service Users",          icon: Users2,           testid: "cmd-static-clients" },
  { path: "/care-plans",          label: "Care Plans",             icon: ClipboardCheck,   testid: "cmd-static-care-plans" },
  { path: "/risk-register",       label: "Risk Register",          icon: ShieldCheck,      testid: "cmd-static-risks" },
  { path: "/rota",                label: "Rota & Visits",          icon: ArrowRight,       testid: "cmd-static-rota" },
  { path: "/mar-chart",           label: "MAR Chart",              icon: ArrowRight,       testid: "cmd-static-mar" },
  { path: "/incidents",           label: "Incidents",              icon: ArrowRight,       testid: "cmd-static-incidents" },
  { path: "/audits",              label: "Audits & QA",            icon: ArrowRight,       testid: "cmd-static-audits" },
  { path: "/training",            label: "Training & Compliance",  icon: ArrowRight,       testid: "cmd-static-training" },
  { path: "/outcomes",            label: "Outcomes",               icon: ArrowRight,       testid: "cmd-static-outcomes" },
  { path: "/supervisions",        label: "Supervisions",           icon: ArrowRight,       testid: "cmd-static-supervisions" },
  { path: "/calendar",            label: "Appointments",           icon: ArrowRight,       testid: "cmd-static-appointments" },
  { path: "/staff-schedule",      label: "Staff Schedule",         icon: ArrowRight,       testid: "cmd-static-staff-schedule" },
  { path: "/time-off-requests",   label: "Leave & Holidays",       icon: ArrowRight,       testid: "cmd-static-leave" },
  { path: "/sites",               label: "Sites",                  icon: Building2,        testid: "cmd-static-sites" },
  { path: "/users",               label: "Staff & Users",          icon: ArrowRight,       testid: "cmd-static-users" },
  { path: "/migration",           label: "Data Migration",         icon: ArrowRight,       testid: "cmd-static-migration" },
];

export default function CommandPalette() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [carePlans, setCarePlans] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const nav = useMemo(() => (NAV || []).filter((n) => !!n.path && n.path !== "/"), []);

  // Global hotkey — Cmd/Ctrl + K.
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Lazy-load data the first time the palette opens.
  const hydrate = useCallback(async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        api.get("/clients/directory").catch(() => ({ data: { clients: [] } })),
        api.get("/care-planning").catch(() => ({ data: [] })),
      ]);
      setClients(cRes.data?.clients || []);
      const planRows = pRes.data?.items || pRes.data || [];
      setCarePlans(Array.isArray(planRows) ? planRows : []);
    } catch { /* non-fatal */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    if (open && !loaded) hydrate();
  }, [open, loaded, hydrate]);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <VisuallyHidden.Root>
        <DialogTitle>Quick jump</DialogTitle>
        <DialogDescription>Search modules, service users and care plans, then press Enter to open.</DialogDescription>
      </VisuallyHidden.Root>
      <div data-testid="command-palette">
        <CommandInput placeholder="Search modules, service users, care plans…" data-testid="command-input" />
        <CommandList data-testid="command-list">
          <CommandEmpty data-testid="command-empty">No matches. Press Esc to close.</CommandEmpty>

          {/* Modules — pulled from the same NAV config the sidebar uses */}
          <CommandGroup heading="Modules">
            {nav.map((n) => (
              <CommandItem
                key={n.path}
                onSelect={() => go(n.path)}
                value={`module ${n.label} ${n.path}`}
                data-testid={`cmd-nav-${n.testid || n.path.replace("/", "")}`}
              >
                <LayoutDashboard className="w-4 h-4 text-slate-500" />
                <span>{n.label}</span>
                <CommandShortcut>{n.path}</CommandShortcut>
              </CommandItem>
            ))}
            {STATIC_ROUTES.filter((r) => !nav.some((n) => n.path === r.path)).map((r) => {
              const Icon = r.icon;
              return (
                <CommandItem
                  key={r.path}
                  onSelect={() => go(r.path)}
                  value={`module ${r.label} ${r.path}`}
                  data-testid={r.testid}
                >
                  <Icon className="w-4 h-4 text-slate-500" />
                  <span>{r.label}</span>
                  <CommandShortcut>{r.path}</CommandShortcut>
                </CommandItem>
              );
            })}
          </CommandGroup>

          {clients.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading={`Service Users (${clients.length})`}>
                {clients.slice(0, 200).map((c) => {
                  const display = c.preferred_name || c.full_name || "Service user";
                  const meta = [c.site_name, c.dob].filter(Boolean).join(" · ");
                  return (
                    <CommandItem
                      key={c.id}
                      onSelect={() => go(`/clients/${c.id}`)}
                      value={`serviceuser ${display} ${c.full_name || ""} ${meta}`}
                      data-testid={`cmd-client-${c.id}`}
                    >
                      <Users2 className="w-4 h-4 text-sky-600" />
                      <span className="truncate">{display}</span>
                      {meta && <CommandShortcut>{meta}</CommandShortcut>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          )}

          {carePlans.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading={`Care Plans (${carePlans.length})`}>
                {carePlans.slice(0, 100).map((p) => {
                  const title = p?.data?.title || p?.data?.plan_type || p?.title || p?.plan_type || "Care Plan";
                  return (
                    <CommandItem
                      key={p.id}
                      onSelect={() => go(`/clients/${p.client_id}/care-planning`)}
                      value={`careplan ${title} ${p.client_name || ""}`}
                      data-testid={`cmd-careplan-${p.id}`}
                    >
                      <ClipboardCheck className="w-4 h-4 text-emerald-600" />
                      <span className="truncate">{title}</span>
                      {p.client_name && <CommandShortcut>{p.client_name}</CommandShortcut>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </div>
    </CommandDialog>
  );
}

/** Small button that lives in the TopBar and opens the palette on click.
 *  We render the button here so we can share the open-state via a ref-less
 *  keyboard event, but for click-to-open we simulate the hotkey by
 *  dispatching a synthetic Cmd/Ctrl+K. */
export function CommandPaletteTrigger() {
  const trigger = () => {
    const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
    document.dispatchEvent(ev);
  };
  return (
    <button
      type="button"
      onClick={trigger}
      data-testid="topbar-command-open"
      title="Quick jump (⌘K)"
      className="inline-flex items-center gap-2 h-7 pl-2 pr-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-300 hover:border-sky-300 hover:text-sky-700 transition-colors text-[11px]"
    >
      <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="9" cy="9" r="6"/><path d="m17 17-3.5-3.5"/></svg>
      <span className="hidden md:inline">Quick jump</span>
      <kbd className="hidden md:inline-flex items-center h-4 px-1 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[10px] font-mono tabular-nums text-slate-600 dark:text-slate-300">⌘K</kbd>
    </button>
  );
}
